import React, { Component } from "react";
import { connect } from "react-redux";
import API_Root from "../Constants/API_Root";
import QuestionAnswer from "../QuestionAnswer";
import { toggleToast } from "../Actions/ToggleToast";
import { ThreeDots } from "react-loader-spinner";
import { generateUniqueSessionId } from "../UniqueId";
import Login from "../App/Login";
import Logout from "../App/Logout";
import ChatSessionList from "../App/ChatSessionList";
import { withRouter } from "react-router-dom";
import { Redirect } from "react-router-dom";
import { auth } from "../Authentication/firebase/firebase";
import { Link } from "react-router-dom";
import { authFetch } from "../Authentication/authService";

const hardcodedQA = {
  "What is in the knowledge base?": `My knowledge base consists of thousands of documents making it the world’s most comprehensive collection of:
<ul>
    <li>IFI, DFI, EP Banks and Export Credit Agency project documents</li>
    <li>Policies and guidance notes</li>
    <li>National statistics</li>
    <li>Academic and practitioner research documents</li>
    <li>Research</li>
    <li>E&S legislation</li>
    <li>Project administration manuals, procurement plans and loan agreements</li>
</ul>
The project documents category includes all impact assessments, appendices and plans that are disclosed and publicly available for the project. Where the original language of the document is not English, we translated it before adding it to the knowledge base.

Our knowledge base is updated on a regular basis with the latest disclosed information on projects, statistics, policies and more!

We welcome your feedback on what you’d like to see added to the knowledge base, but now go ahead and check to see what myESRA knows about your projects!`,

  "What are the main gaps between the expropriation legislation in North Macedonia and EBRDs Performance Requirement 5?": `<h1>Gaps Analysis between Macedonian Legal Framework & EBRD Requirements</h1>
<p>The key gaps between the national legislation of North Macedonia and EBRD’s PR5 relevant for this Project are summarised below. This RF (including the entitlements matrix) contains measures to bridge these gaps:</p>
<ul>
    <li><strong>Informal Land Rights:</strong> Macedonian law focuses on compensation for formal land rights, whereas EBRD also covers compensation, livelihood restoration or other assistance, assistance for resettlement, and consultation for persons with no formal recognisable legal right or claim to land they occupy.</li>
    <li><strong>Participation and Consultation:</strong> EBRD PR5 sets greater requirements than Macedonian law for giving affected persons the opportunity to participate in the negotiation of the compensation packages, eligibility requirements, livelihood restoration and resettlement assistance.</li>
    <li><strong>Socio-Economic Surveys:</strong> Conducting socio-economic surveys to assess impacts on livelihoods is required by EBRD, but not required by Macedonian law.</li>
    <li><strong>Vulnerable Individuals & Groups:</strong> Macedonian law does not stipulate special measures relating to vulnerable groups. EBRD, however, requires particular attention to be paid to vulnerable groups, including during consultation, identification of entitlements and appropriate resettlement assistance.</li>
    <li><strong>Resettlement Frameworks & Plans:</strong> Macedonian law does not require the preparation of resettlement frameworks/plans. These are required for EBRD financed projects involving land acquisition and resettlement resulting in physical or economic displacement.</li>
    <li><strong>Valuation of Expropriated Assets:</strong> EBRD states that compensation for loss of assets is calculated at full replacement cost, i.e., market value of the assets plus transaction costs, and that depreciation of structures and assets should not be taken into account. However, when it comes to a facility which because of its nature is not in circulation and for which a market value is not determined (wells, fence and retaining walls and similar), Macedonian law states that compensation is established according to the market value of the material and the labour necessary for construction of facilities, decreased by the relevant amortization amount.</li>
    <li><strong>Grievance Mechanisms:</strong> Although Macedonian law establishes rights of affected citizens (those with formal land rights) to appeal to Courts during the expropriation process, it does not require development of specific grievance mechanisms. EBRD, however, requires grievance mechanisms to be set up as early as possible in the process, to receive and address in a timely fashion specific concerns about compensation and relocation that are raised by displaced persons, including a recourse mechanism designed to resolve disputes in an impartial manner. A summary of complaints and the measures taken to resolve them should be made public on a regular basis.</li>
    <li><strong>Access to Land/Property Prior to Payment of Compensation:</strong> There are several options for the Proposer of Expropriation to acquire the right of accessing land/property prior to payment of compensation, under the Expropriation Law, Law on Assessment and the Law on Construction.</li>
    <li><strong>Monitoring & Evaluation:</strong> There are no requirements for monitoring and evaluation of the resettlement and the livelihood restoration process and outcomes under Macedonian legislation.</li>
</ul>`,

  "List the assessment criteria to identify indigenous peoples according to ADB.": `<h1>According to the ADB, the characteristics that make someone an indigenous person are:</h1>
<ul>
    <li>(i) Self-identification as members of a distinct indigenous cultural group and recognition of this community by others</li>
    <li>(ii) Collective attachment to geographically distinct habitats or ancestral territories in the project area and to the natural resources in these habitats</li>
    <li>(iii) Customary cultural, economic, social, or political institutions that are separate from those of the dominant society and culture</li>
    <li>(iv) A distinct language, often different from the official language of the country or region</li>
</ul>`,

  "What is the total cost of the Masrik Solar project's LRP including the line items?": `<h2>Number/Value:</h2>
<p>The total LRP implementation budget for the Masrik Solar project is AMD 252,639,458.72 or US$ 517,703.81.</p>
<p>This includes the compensation budget, livelihood restoration measures budget, community development measures (CDM) budget, and budget for consultancy services. Here is a breakdown of the line items and costs:</p>
<ul>
    <li><strong>Compensation Budget:</strong>
        <ul>
            <li>Land acquisition (tower + interconnection equipment): AMD 348,942.43 (US$ 715.05)</li>
            <li>Land acquisition (Additional land for PV plant 32.7 ha): AMD 47,029,104.00 (US$ 96,371.11)</li>
            <li>Land Compensation for Servitude (Total for 20 years): AMD 75,839,000.43 (US$ 155,407.79)</li>
            <li>Land Compensation for Privately Rented Lands: AMD 1,006,761.29 (US$ 2,063.04)</li>
            <li>Crops: AMD 68,427.05 (US$ 140.22)</li>
            <li>Sub-Total (Compensation Budget): AMD 124,292,235.20 (US$ 254,697.20)</li>
        </ul>
    </li>
</ul>
<ul>
    <li><strong>Livelihood Restoration Measures Budget:</strong>
        <ul>
            <li>Annual Allowance for Rented Lands (Total for 20 years): AMD 6,600,000.00 (US$ 13,524.59)</li>
            <li>Registration Cost: AMD 2,259,000.00 (US$ 4,629.10)</li>
            <li>Vulnerability Allowance: AMD 330,000.00 (US$ 676.23)</li>
            <li>Sub-Total (Livelihood Restoration Measures Budget): AMD 9,189,000.00 (US$ 18,829.92)</li>
        </ul>
    </li>
</ul>
<ul>
    <li><strong>Community Development Measures (CDM) Budget:</strong>
        <ul>
            <li>Compensation for land donation (97.4ha): AMD 80,000,000.00 (US$ 163,934.43)</li>
            <li>Donation of an Excavator: AMD 13,311,000.00 (US$ 27,276.64)</li>
            <li>Sub-Total (CDM Budget): AMD 93,311,000.00 (US$ 191,211.07)</li>
        </ul>
    </li>
</ul>
<ul>
    <li><strong>Budget for Consultancy Services: AMD 2,880,000.00</strong></li>
</ul>
<br />
<p>The budget summary also includes line items with annual payments:</p>
<ul>
    <li>Servitude Payment: AMD 75,839,000.43 (US$ 155,407.79 for 20 years)</li>
    <li>Annual Allowance for Rented Lands: AMD 6,600,000.00 (US$ 13,524.59 for 20 years)</li>
    <li>Total for these annual payments: AMD 82,439,000.43 (US$ 168,932.38 for 20 years).</li>
</ul>

<p>The exchange rate used is 1 USD = 488 AMD as of March 27, 2019.</p>
<p>More information:</p>
<ul>
    <li>These details were obtained from the "LIVELIHOOD RESTORATION PLAN - FRV MASRIK CJSC" report, specifically from the sections on different budget summaries and tables related to the various compensation and development measures associated with the project.</li>
</ul>
`,
};

class AppIndex extends Component {
  constructor(props) {
    super(props);
    this.state = {
      prompt: "",
      conversationId: 0,
      currentChatSessionId: null,
      chatHistory: [],
      fetching: false,
      abortController: null,
      error_msg: "",
      userEmail: "",
      subscription: null,
      showSubscriptionAlert: false,
      subscriptionActive: false,
      ip_address: "",
      allowedEmails: [],
      initVectorDBMessages: [],
      initVectorDBDoneMessage: "",
      sessionId: "",
      pocAccess: false,
      isSidebarVisible: true,
      isQuestionTypeVisible: false,
      showLoginModal: false,
      canvasMode: "chat", // default,
      isLoggedIn: false,
      firstName: "None",
      lastName: "None",
      fullname: "None",
      shouldRedirectToProfile: false,
      selectedModel: "gpt-4o-mini",
      pineconeIndex: "new-index",
      promptType: "",
      uploading: false,
      uploadingCount: 0,
      selectedFiles: [], // <-- files to display in the UI,
      selectedFileid: [],
      questionType: "general",
      systemPrompt: `You are a helpful assistant. When asked a question, use the sources given to answer to the best of your ability. Try to get your answer from the provided files.`,
    };
    this.textareaRef = React.createRef();
  }
  componentDidMount() {
    console.log(this.selectedFiles, "selectedFiles");
    fetch("https://get.geojs.io/v1/ip/geo.json")
      .then((res) => res.json())
      .then((data) => {
        this.setState({ ip_address: data.ip });
      })
      .catch((error) => {
        console.error("Error fetching IP information:", error);
      });
    // Check if sessionId already exists in localStorage
    let sessionId = localStorage.getItem("sessionId");

    // If sessionId doesn't exist, generate a new one and store it in localStorage
    sessionId = generateUniqueSessionId();
    localStorage.setItem("sessionId", sessionId);

    const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";
    if (!isLoggedIn) {
      this.props.history.push("/login");
    }
    this.setState({ isLoggedIn });

    // Set the sessionId in the component state
    this.setState({
      sessionId: sessionId,
    });

    auth.onAuthStateChanged((user) => {
      if (user) {
        this.setState({
          isLoggedIn: true,
          userEmail: user.email,
        });
        this.fetchProfileInformation(this.state.userEmail);
      } else {
        this.setState({
          isLoggedIn: false,
          userEmail: null,
        });
      }
    });

    if (this.props.showSidebarOnLoad) {
      this.setState({ isSidebarVisible: true });
    }
    if (this.props.chatgptPromptType) {
      this.setState({ promptType: "chatgpt" });
    }
    if (this.props.categoryPromptType) {
      this.setState({ promptType: "category" });
    }
    if (this.props.individualPromptType) {
      this.setState({ promptType: "individual" });
      this.setState({ isQuestionTypeVisible: true });
    }
    this.fetchTesterList();
  }
  handleSuccessfulLogin = (_userEmail) => {
    this.setState({ userEmail: _userEmail });
    this.setState({ isLoggedIn: true });
  };
  handleLogout = () => {
    this.setState({
      userEmail: null,
      firstName: "None",
      lastName: "None",
      chatHistory: [],
      isLoggedIn: false,
    });
  };
  toggleLoginModal = () => {
    this.setState((prevState) => ({
      showLoginModal: !prevState.showLoginModal,
    }));
  };
  handleModelChange = (event) => {
    this.setState({ selectedModel: event.target.value });
  };
  fetchTesterList = () => {
    authFetch(API_Root + "team/get-tester-list")
      .then((response) => response.json())
      .then((data) => {
        if (data && data.testers) {
          // Extract emails from the tester list
          const allowedEmails = data.testers.map((tester) => tester.email);
          this.setState({ allowedEmails });
        }
      })
      .catch((error) => {
        console.error("Error fetching tester list:", error);
      });
  };
  initVectorDB = () => {
    fetch(API_Root + "init-vectordb", {
      method: "GET",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json", // Set content type to JSON
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        // Handle the success response if needed
        console.log("init-vectordb response:", data);
      })
      .catch((error) => {
        // Handle the error if needed
        console.error("Error in initVectorDB:", error);
      });
  };
  fetchProfileInformation = (email) => {
    authFetch(API_Root + "users/get-profile-information", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: email,
        ip_address: this.state.ip_address,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        this.setState(
          {
            firstName: data.myesra_subscription.first_name || "None",
            lastName: data.myesra_subscription.last_name || "None",
            fullname: data.myesra_subscription.full_name || "None",
            subscription: data.myesra_subscription.subscription,
            pocAccess: data.myesra_subscription.access || false,
          },
          () => {
            const { fullname, subscription } = this.state;
            // console.log(firstName, "firstname", lastName, "lastnaame")
            if (fullname === "None") {
              this.setState({ shouldRedirectToProfile: true });
            }
            if (subscription === null) {
              this.toggleSubscriptionAlert(true);
            }

            let isActive = false;
            if (subscription) {
              const now = new Date();
              const utcPlanStartDate = new Date(
                subscription.planDetails.planStartDate
              );
              const planStartDate = new Date(
                utcPlanStartDate.getTime() -
                  utcPlanStartDate.getTimezoneOffset() * 60000
              );

              // Log dates for debugging
              console.log("Current Date:", now);
              console.log("Plan Start Date:", planStartDate);

              // Check if now is greater than planStartDate, isActive if now < planEndDate
              if (now >= planStartDate) {
                if (subscription.planType === "Date") {
                  const planEndDate = new Date(
                    subscription.planDetails.planEndDate
                  );
                  console.log("Plan End Date:", planEndDate);
                  isActive = now <= planEndDate;
                } else {
                  const timeDiff = now.getTime() - planStartDate.getTime();
                  console.log("Time Difference (ms):", timeDiff);

                  // Perform checks for Weekly, Monthly, and Annual plans
                  if (subscription.planType === "Weekly") {
                    isActive = timeDiff <= 7 * 24 * 60 * 60 * 1000; // 7 days
                  } else if (subscription.planType === "Monthly") {
                    isActive = timeDiff <= 30 * 24 * 60 * 60 * 1000; // 30 days
                  } else if (subscription.planType === "Annual") {
                    isActive = timeDiff <= 365 * 24 * 60 * 60 * 1000; // 365 days
                  }
                }
              }
            }

            // Log the final isActive status for debugging
            console.log("Is Active:", isActive);

            this.setState({ subscriptionActive: isActive }, () => {
              if (!this.state.subscriptionActive) {
                this.toggleSubscriptionAlert(true);
                this.removeSubscription();
              }
            });
          }
        );
      })
      .catch((error) => {
        console.error("Error fetching profile information:", error);
      });
  };

  removeSubscription = () => {
    const { userEmail } = this.state;
    authFetch(API_Root + "subscription/remove-subscription", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: userEmail,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        console.log("Subscription status updated:", data);
      })
      .catch((error) => {
        console.error("Error removing subscription:", error);
      });
  };
  delAssistantFiles = () => {
    fetch(API_Root + "files/delfiles-assistant", {
      method: "GET",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json", // Set content type to JSON
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        console.log("files/delfiles-assistant response:", data);
      })
      .catch((error) => {
        console.error("Error in deleteAssistantFiles:", error);
      });
  };
  handleLogin = () => {
    const username = this.state.username;
    const password = this.state.password;

    if (
      username === "woyeradfghgh4#@#56456456" &&
      password === "woyera12456234dffgdfg1134##3"
    ) {
      this.setState({ isLoggedIn: true });
    } else {
      this.props.toggleToast({
        show: true,
        type: "error",
        message: "Please enter both username and password.",
      });
    }
  };

  handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
  
      const isDisabled =
        this.state.fetching ||
        this.state.uploading ||
        this.state.subscription === null ||
        this.state.subscriptionActive === false;
  
      if (!isDisabled && this.state.prompt.trim() !== "") {
        this.getChatResp();
      }
    }
  };

  handleInputChange = (event) => {
    const val = event.target.value;
    const elmName = event.target.name;

    this.setState({ [elmName]: val, emailValidationError: "" });
  };
  createNewChatSession = () => {
    const { userEmail } = this.state;
    if (!userEmail) {
      alert("Please login to create a new chat session.");
      return;
    }

    authFetch(API_Root + "session/create-chat-session", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email: userEmail }),
    })
      .then((response) => response.json())
      .then((data) => {
        console.log("New chat session created:", data);
        this.setState({
          chatHistory: [],
          currentChatSessionId: data["chat_session_id"],
        });
        this.refreshChatSessions();
      })
      .catch((error) => {
        console.error("Error creating new chat session:", error);
      });
  };
  refreshChatSessions = () => {
    this.chatSessionListRef.fetchChatSessions();
  };
  handleSendFeedback = (conversationId, isHelpful, comment) => {
    authFetch(API_Root + "users/record-feedback", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        conversation_id: conversationId,
        feedback: isHelpful,
        feedback_comments: comment,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        this.props.toggleToast({
          show: true,
          type: "success",
          message: "SUCCESS: Feedback sent successfully.",
        });
      })
      .catch((error) => {
        this.props.toggleToast({
          show: true,
          type: "error",
          message: "ERROR: Failed to send feedback.",
        });
      });
  };
  handleCopyResponse = async (responseText, conversationId) => {
    try {
      // Use the Clipboard API to copy the text
      await navigator.clipboard.writeText(responseText);

      // Prepare the analytics data
      const analyticsData = {
        actionType: "copy_response",
        chatSessionId: this.state.currentChatSessionId,
        conversationId: conversationId,
        copiedResponse: responseText,
      };

      // Make the POST request to the backend
      await authFetch(API_Root + "chat/store-analytics-info", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(analyticsData),
      });

      console.log("Copy action and analytics logging succeeded.");
    } catch (error) {
      console.error("Error handling copy response:", error);
    }
  };

  refreshChat = (prompt, conversationId) => {
    this.setState({ prompt, conversationId }, this.getChatResp);
  };

  toggleVoiceInput = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert("Sorry, your browser does not support speech recognition.");
      return;
    }

    // If already listening, stop it
    if (this.recognition && this.state.isListening) {
      this.recognition.stop();
      this.setState({ isListening: false });
      console.log("🎤 Stopped listening.");
      return;
    }

    // Start listening
    this.recognition = new SpeechRecognition();
    this.recognition.lang = "en-IN";
    this.recognition.interimResults = true; // ✅ Show live transcription
    this.recognition.continuous = true; // ✅ Keep listening until manually stopped

    let finalTranscript = this.state.prompt || "";

    this.recognition.onstart = () => {
      this.setState({ isListening: true });
      console.log("🎤 Voice recognition started. Speak now...");
    };

    this.recognition.onresult = (event) => {
      let interimTranscript = "";
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += " " + transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      // Update textarea live while speaking
      this.setState({
        prompt: `${finalTranscript} ${interimTranscript}`.trim(),
      });
    };

    this.recognition.onerror = (event) => {
      console.error("Speech recognition error:", event.error);
      if (event.error === "not-allowed") {
        alert("Microphone access was denied. Please allow mic permissions.");
      }
      this.setState({ isListening: false });
    };

    this.recognition.onend = () => {
      console.log("🎤 Voice recognition ended.");
      this.setState({ isListening: false });
    };

    this.recognition.start();
  };

  getChatResp = async () => {
    const answerText = hardcodedQA[this.state.prompt];
    if (answerText) {
      this.handleSampleButtonClick(this.state.prompt);
      this.setState({ prompt: "" });
      return;
    }

    this.setState({ fetching: true, answer: "" });

    const chatIndex = this.state.chatHistory.length;

    // Add temporary chat entry
    this.setState((prev) => ({
      chatHistory: [
        ...prev.chatHistory,
        {
          prompt: prev.prompt,
          answer: "",
          conversation_id: prev.conversationId || 0,
          file_names: [],
        },
      ],
      prompt: "",
    }));

    let fullAnswer = "";

    // STREAM HANDLER
    const streamAndType = async (
      reader,
      decoder,
      parseLine,
      addSpace = false
    ) => {
      let buffer = "";

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();

        for (const part of parts) {
          let chunk = parseLine(part);
          if (!chunk) continue;

          if (addSpace) chunk += " ";

          fullAnswer += chunk;

          for (let i = 0; i < chunk.length; i++) {
            await new Promise((res) => setTimeout(res, 12));
            this.setState((prev) => {
              const updated = [...prev.chatHistory];
              updated[chatIndex].answer += chunk[i];
              return { chatHistory: updated };
            });
          }
        }
      }
    };

    try {
      // ============================================
      // 📂 CASE 1: Files selected → send to /api/search
      // ============================================
      if (this.state.selectedFiles.length > 0) {
        const searchResponse = await authFetch(API_Root + "files/api/search", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "text/event-stream",
          },
          body: JSON.stringify({
            query: this.state.prompt,
            session_id: this.state.sessionId || null,
          }),
        });

        if (searchResponse.body) {
          await streamAndType(
            searchResponse.body.getReader(),
            new TextDecoder("utf-8"),
            (part) =>
              part.startsWith("data:") ? part.replace("data:", "").trim() : "",
            true // adds space
          );
        }

        this.setState({ fetching: false });
        return;
      }

      // ============================================
      // 💬 CASE 2: Normal chat flow
      // ============================================

      const requestData = {
        prompt: this.state.prompt,
        chat_history: this.state.chatHistory,
        sessionId: this.state.sessionId,
        selectedModel: this.state.selectedModel,
        pineconeIndex: this.state.pineconeIndex,
        systemPrompt: this.state.systemPrompt,
        promptType: this.state.promptType,
        questionType: this.state.questionType,
        email: this.state.userEmail,
        conversation_id: this.state.conversationId,
        chatSessionId: this.state.currentChatSessionId,
      };

      const sseResponse = await authFetch(API_Root + "chat/chat-sse", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
        },
        body: JSON.stringify({ prompt: this.state.prompt }),
      });

      if (sseResponse.body) {
        await streamAndType(
          sseResponse.body.getReader(),
          new TextDecoder("utf-8"),
          (part) => {
            if (!part.startsWith("data:")) return "";
            try {
              const parsed = JSON.parse(part.replace("data:", "").trim());
              return parsed.content || "";
            } catch {
              return "";
            }
          }
        );
      }

      // MAIN CHAT STREAM
      const chatResponse = await authFetch(API_Root + "chat/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
        },
        body: JSON.stringify(requestData),
      });

      let extractedFiles = [];

      if (chatResponse.body) {
        await streamAndType(
          chatResponse.body.getReader(),
          new TextDecoder("utf-8"),
          (part) => {
            if (!part.startsWith("data:")) return "";
            try {
              const parsed = JSON.parse(part.replace("data:", "").trim());

              if (parsed.type === "content") return parsed.message;

              if (
                parsed.type === "debug" &&
                parsed.message.includes("Extracted filenames")
              ) {
                const match = parsed.message.match(/Extracted filenames: (.*)/);
                if (match && match[1]) {
                  extractedFiles = JSON.parse(match[1].replace(/'/g, '"')).map(
                    (src) => ({ source: src, content: "" })
                  );
                }
              }
            } catch {}
            return "";
          }
        );
      }

      // Update file list for chat entry
      this.setState((prev) => {
        const updated = [...prev.chatHistory];
        updated[chatIndex].file_names = extractedFiles;
        return { chatHistory: updated, fetching: false };
      });
    } catch (err) {
      console.error("Chat error:", err);
      this.setState({ fetching: false });
    }
  };

  handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // 1️⃣ Create stable temp entries
    const tempEntries = files.map((f) => ({
      name: f.name,
      file_id: null,
      status: "uploading",
    }));

    // 2️⃣ Freeze the previous UI state to avoid async overwrite
    const previousList = [...this.state.selectedFiles];
    const baseList = [...previousList, ...tempEntries];

    // Update UI immediately
    this.setState({ selectedFiles: baseList });

    this.setState({
      uploading: true,
      uploadingCount: files.length,
    });

    // 3️⃣ Build FormData safely
    const formData = new FormData();
    files.forEach((file) => {
      const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
      const cleanName =
        file.name.substring(0, file.name.lastIndexOf(".")) + ext;

      formData.append(
        "files",
        new File([file], cleanName, { type: file.type })
      );
    });

    try {
      const res = await authFetch(API_Root + "files/api/files/upload", {
        method: "POST",
        body: formData,
      });

      const result = await res.json();

      if (!res.ok || !result.stats || result.success === false) {
        alert(result.error || "Upload failed");
        this.setState({ uploading: false });
        return;
      }

      const uploaded = result.stats.uploaded;
      // uploaded = [{ file: "abc.pdf", file_id: "XYZ" }]

      // 4️⃣ Merge uploaded file_ids correctly using filename match
      const finalList = baseList.map((item) => {
        const found = uploaded.find((u) => u.file === item.name);
        return found
          ? { ...item, file_id: found.file_id, status: "success" }
          : item;
      });

      this.setState({ selectedFiles: finalList });
    } catch (err) {
      console.error(err);
      alert("Upload error");
    }

    this.setState({ uploading: false, uploadingCount: 0 });
  };

  removeSelectedFile = async (index) => {
    const file = this.state.selectedFiles[index];
    console.log(file, "file");

    // 1️⃣ If file not uploaded yet → just remove from UI
    if (!file.file_id) {
      const updated = [...this.state.selectedFiles];
      updated.splice(index, 1);
      this.setState({ selectedFiles: updated });
      return;
    }

    if (!window.confirm(`Delete file "${file.name}"?`)) return;

    try {
      const res = await authFetch(
        API_Root + `files/api/files/${file.file_id}`,
        {
          method: "DELETE",
        }
      );

      const result = await res.json();

      if (!res.ok || result.success === false) {
        alert(result.message || "Failed to delete file");
        return;
      }

      const updated = [...this.state.selectedFiles];
      updated.splice(index, 1);
      this.setState({ selectedFiles: updated });
    } catch (err) {
      console.error(err);
      alert("Error deleting file");
    }
  };

  //function to cancel fetch
  cancelFetch = () => {
    if (this.state.abortController) {
      this.state.abortController.abort(); // Abort the fetch request
      this.setState({ fetching: false, abortController: null }); // Reset the state
    }
  };

  handleSampleButtonClick = (promptText, fileList) => {
    const answerText = hardcodedQA[promptText];
    let source_documents = fileList.map((fileName) => ({
      content: "",
      source: fileName,
    }));
    console.log(source_documents);
    if (answerText) {
      const mockConversationId = Date.now();

      const hardcodedEntry = {
        prompt: promptText,
        answer: answerText,
        file_names: source_documents,
        conversation_id: mockConversationId,
      };

      // Append the constructed entry to chatHistory
      this.setState((prevState) => ({
        chatHistory: [...prevState.chatHistory, hardcodedEntry],
      }));

      this.setState({ prompt: "" });
    } else {
      // Handle other prompts or actions
      console.log("Not a hardcoded prompt.");
    }
  };
  toggleDisplayBox = () => {
    this.setState((prevState) => ({
      isDisplayBoxExpanded: !prevState.isDisplayBoxExpanded,
    }));
  };
  handleChatSessionSelected = (conversations, chatSessionId) => {
    // Update the state with the fetched conversations
    this.setState({
      chatHistory: conversations,
      currentChatSessionId: chatSessionId,
    });
  };
  toggleSidebar = () => {
    this.setState((prevState) => ({
      isSidebarVisible: !prevState.isSidebarVisible,
    }));
  };

  handlePineconeIndexChange = (event) => {
    const pineconeIndex =
      event.target.value === "old-index" ? "old-index" : "new-index";
    this.setState({ pineconeIndex });
  };

  toggleSubscriptionAlert = (show) => {
    this.setState({ showSubscriptionAlert: show });
  };
  render() {
    const {
      shouldRedirectToProfile,
      showSubscriptionAlert,
      pocAccess,
      isSidebarVisible,
    } = this.state;

    if (shouldRedirectToProfile) {
      return <Redirect to="/profile" />;
    }

    const questionTypeDropdown = this.state.isQuestionTypeVisible ? (
      <select
        className="form-select form-select-sm me-2"
        style={{ maxWidth: "180px" }}
        value={this.state.questionType}
        onChange={(e) => this.setState({ questionType: e.target.value })}
      >
        <option value="number_value">NUMBER/VALUE</option>
        <option value="structure_template">TEMPLATE</option>
        <option value="design_survey">DESIGN/SURVEY</option>
        <option value="gap_analysis">GAP ANALYSIS</option>
        <option value="general">GENERAL</option>
      </select>
    ) : null;

    return (
      <div
        className="container-fluid p-0"
        style={{ height: "100vh", overflow: "hidden" }}
      >
        <div className="row g-0" style={{ height: "100%" }}>
          {/* ================= SIDEBAR ================= */}
          {isSidebarVisible && (
            <div
              className="col-12 col-md-3 col-lg-2 bg-light border-end d-flex flex-column"
              style={{ height: "100vh", overflow: "hidden" }}
            >
              <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
                <Link
                  to="/"
                  className="fw-bold fs-5 text-success text-decoration-none"
                >
                  myESRA
                </Link>

                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={this.toggleSidebar}
                >
                  ✕
                </button>
              </div>

              <div className="p-3">
                <button
                  className="btn btn-success w-100"
                  onClick={this.createNewChatSession}
                  disabled={
                    this.state.fetching ||
                    this.state.subscription === null ||
                    this.state.subscriptionActive === false
                  }
                >
                  New Chat
                </button>
              </div>

              <div className="flex-grow-1 px-3" style={{ overflowY: "auto" }}>
                <small className="text-muted fw-bold">PREVIOUS 30 DAYS</small>

                <ChatSessionList
                  ref={(el) => (this.chatSessionListRef = el)}
                  onChatSessionSelected={this.handleChatSessionSelected}
                  email={this.state.userEmail}
                />
              </div>

              <div className="border-top p-3">
                <Link
                  to="/faqs"
                  className="btn btn-outline-secondary w-100 mb-2"
                >
                  Help
                </Link>

                {pocAccess && (
                  <Link
                    to="/privatesra"
                    className="btn btn-outline-primary w-100 mb-2"
                  >
                    privatESRA
                  </Link>
                )}

                {this.state.userEmail ? (
                  <Logout
                    onLogout={this.handleLogout}
                    email={this.state.userEmail}
                    firstName={this.state.firstName}
                    lastName={this.state.lastName}
                  />
                ) : (
                  <button
                    className="btn btn-dark w-100"
                    onClick={() => this.props.history.push("/login")}
                  >
                    Login Here
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ================= MAIN CHAT AREA ================= */}
          <div
            className="col d-flex flex-column"
            style={{ height: "100vh", overflow: "hidden" }}
          >
            {!isSidebarVisible && (
              <button
                className="btn btn-sm btn-outline-secondary position-absolute"
                style={{ top: "15px", left: "15px", zIndex: 1050 }}
                onClick={this.toggleSidebar}
              >
                ☰
              </button>
            )}

            {showSubscriptionAlert && (
              <div className="alert alert-warning text-center rounded-0 mb-0">
                <strong>WARNING:</strong> It seems you aren't subscribed! &nbsp;
                <Link to="/pricing">Click here to subscribe.</Link>
              </div>
            )}

            {/* ================= CHAT CONTENT ================= */}
            <div
              className="flex-grow-1 p-3 bg-white"
              style={{ overflowY: "auto" }}
            >
              {this.state.chatHistory.length === 0 && !this.state.fetching ? (
                <div className="text-center mt-5">
                  <h2 className="fw-bold mb-4">What are you looking for?</h2>

                  <div className="d-grid gap-3 col-12 col-md-8 mx-auto">
                    <button
                      className="btn btn-outline-secondary text-start"
                      onClick={() =>
                        this.handleSampleButtonClick(
                          "What is in the knowledge base?",
                          []
                        )
                      }
                    >
                      <strong>What is in the knowledge base?</strong>
                      <br />
                      Get to know myESRA®
                    </button>

                    <button
                      className="btn btn-outline-secondary text-start"
                      onClick={() =>
                        this.handleSampleButtonClick(
                          "What are the main gaps between the expropriation legislation in North Macedonia and EBRDs Performance Requirement 5?",
                          []
                        )
                      }
                    >
                      <strong>What are the main gaps between</strong>
                      <br />
                      the expropriation legislation in North Macedonia and
                      EBRD’s Performance Requirement 5?
                    </button>

                    <button
                      className="btn btn-outline-secondary text-start"
                      onClick={() =>
                        this.handleSampleButtonClick(
                          "What is the total cost of the Masrik Solar project's LRP including the line items?",
                          []
                        )
                      }
                    >
                      <strong>What is the total cost</strong>
                      <br />
                      of the Masrik Solar project's LRP including the line
                      items?
                    </button>

                    <button
                      className="btn btn-outline-secondary text-start"
                      onClick={() =>
                        this.handleSampleButtonClick(
                          "List the assessment criteria to identify indigenous peoples according to ADB.",
                          []
                        )
                      }
                    >
                      <strong>List the assessment criteria</strong>
                      <br />
                      to identify indigenous peoples according to ADB.
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <QuestionAnswer
                    chatHistory={this.state.chatHistory}
                    onSendFeedback={this.handleSendFeedback}
                    onRefreshChat={this.refreshChat}
                    canvasMode={this.state.canvasMode}
                    handleCopyResponse={this.handleCopyResponse}
                  />

                  {this.state.fetching && (
                    <div className="text-center mb-3">
                      <div className="spinner-border text-primary me-2" />
                      <span className="fw-medium text-muted">Thinking...</span>
                      <button
                        onClick={this.cancelFetch}
                        className="btn btn-sm btn-outline-danger ms-3"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* ================= CHAT INPUT ================= */}
            <div className="border-top bg-light p-3">
              {this.state.selectedFiles.length > 0 && (
                <div className="mb-2 d-flex flex-wrap gap-2">
                  {this.state.selectedFiles.map((file, index) => (
                    <span
                      key={index}
                      className="badge bg-secondary d-flex align-items-center"
                    >
                      {file.name}
                      <button
                        className="btn-close btn-close-white ms-2"
                        style={{ fontSize: "10px" }}
                        onClick={() => this.removeSelectedFile(index)}
                      />
                    </span>
                  ))}
                </div>
              )}

              <div className="d-flex align-items-center">
                {questionTypeDropdown}

                <button
                  className="btn btn-outline-secondary me-2"
                  onClick={this.toggleVoiceInput}
                  disabled={this.state.fetching || this.state.uploading}
                >
                  🎤
                </button>

                <label className="btn btn-outline-secondary me-2 mb-0">
                  📁
                  <input
                    type="file"
                    multiple
                    hidden
                    onChange={this.handleFileUpload}
                  />
                </label>

                <div
                  style={{
                    position: "relative",
                    flexGrow: 1,
                    marginRight: "8px",
                  }}
                >
                  {/* Toggle Inside Input */}
                  <div
                    style={{
                      position: "absolute",
                      left: "8px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      display: "flex",
                      background: "#e9ecef",
                      borderRadius: "20px",
                      padding: "3px",
                      zIndex: 2,
                    }}
                  >
                    {/* Chat */}
                    <span
                      onClick={() => this.setState({ canvasMode: "chat" })}
                      style={{
                        padding: "4px 12px",
                        fontSize: "13px",
                        borderRadius: "18px",
                        cursor: "pointer",
                        background:
                          this.state.canvasMode === "chat"
                            ? "#198754"
                            : "transparent",
                        color:
                          this.state.canvasMode === "chat" ? "#fff" : "#333",
                        transition: "all 0.2s ease",
                        fontWeight: 500,
                      }}
                    >
                      Chat
                    </span>

                    {/* Canvas */}
                    <span
                      onClick={() => this.setState({ canvasMode: "canvas" })}
                      style={{
                        padding: "4px 12px",
                        fontSize: "13px",
                        borderRadius: "18px",
                        cursor: "pointer",
                        background:
                          this.state.canvasMode === "canvas"
                            ? "#198754"
                            : "transparent",
                        color:
                          this.state.canvasMode === "canvas" ? "#fff" : "#333",
                        transition: "all 0.2s ease",
                        fontWeight: 500,
                      }}
                    >
                      Canvas
                    </span>
                  </div>

                  {/* Textarea */}
                  <textarea
                    className="form-control"
                    rows="1"
                    placeholder="Ask a question"
                    name="prompt"
                    value={this.state.prompt}
                    onChange={this.handleInputChange}
                    onKeyDown={this.handleKeyDown}
                    disabled={this.state.fetching || this.state.uploading}
                    style={{
                      paddingLeft: "150px",
                      resize: "none",
                      minHeight: "42px",
                    }}
                  />
                </div>

                <button
                  className="btn btn-success"
                  onClick={this.getChatResp}
                  disabled={
                    this.state.fetching ||
                    this.state.uploading ||
                    this.state.subscription === null ||
                    this.state.subscriptionActive === false
                  }
                >
                  ➤
                </button>
              </div>
              <div
                style={{
                  marginTop: "10px",
                  fontSize: "12px",
                  color: "#6B7280",
                  textAlign: "center",
                  background: "#F9FAFB",
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                  padding: "8px 12px",
                  lineHeight: "1.4",
                }}
              >
                <strong>Note:</strong> Responses are generated by an AI
                assistant and must be verified by an E&amp;S expert.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({});

const mapActionsToProps = { toggleToast: toggleToast };

export default withRouter(
  connect(mapStateToProps, mapActionsToProps)(AppIndex)
);
